import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { artworksAPI, collectionsAPI } from '../services/api';
import { artworks as mockArtworks, collections as mockCollections } from '../data/mock';
import { getImageUrl } from '../utils/imageUrl';
import { formatPrice, parseSizeVariant, getLowestPrice } from '../utils/currency';
import { ChevronLeft, Minus, Plus, Check, Send, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import QuoteRequestModal from '../components/checkout/QuoteRequestModal';
import ProtectedImage from '../components/common/ProtectedImage';
import SEO from '../components/common/SEO';

const ProductPage = () => {
  const { slug } = useParams();
  const { addItem } = useCart();
  const { currency } = useCurrency();
  const [artwork, setArtwork] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [artworksData, collectionsData] = await Promise.all([
          artworksAPI.getAll(),
          collectionsAPI.getAll()
        ]);
        
        const allArtworks = artworksData.length > 0 ? artworksData : mockArtworks;
        const allCollections = collectionsData.length > 0 ? collectionsData : mockCollections;
        
        setArtworks(allArtworks);
        setCollections(allCollections);
        
        const foundArtwork = allArtworks.find(a => a.slug === slug);
        setArtwork(foundArtwork);
        setSelectedSizeIndex(0);
      } catch (error) {
        console.error('Error loading data:', error);
        setArtworks(mockArtworks);
        setCollections(mockCollections);
        const foundArtwork = mockArtworks.find(a => a.slug === slug);
        setArtwork(foundArtwork);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug]);

  // Parse size variants (handles both legacy string format and new object format)
  const sizeVariants = useMemo(() => {
    if (!artwork?.sizes || artwork.sizes.length === 0) {
      return [{ name: 'Standard', price: artwork?.price || 0 }];
    }
    return artwork.sizes.map(s => parseSizeVariant(s, artwork.price));
  }, [artwork]);

  // Get currently selected size variant
  const selectedVariant = sizeVariants[selectedSizeIndex] || sizeVariants[0];

  // Get lowest price for "From" display
  const lowestPrice = useMemo(() => {
    return getLowestPrice(artwork?.sizes, artwork?.price);
  }, [artwork]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="py-20 text-center text-gray-500">Loading...</main>
        <Footer />
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="py-20 text-center">
          <h1 className="text-2xl text-gray-600">Artwork not found</h1>
          <Link to="/collections" className="mt-4 inline-block text-black underline">Browse collections</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const collection = collections.find(c => 
    c.id === artwork.collectionId || c.id === artwork.collection_id
  );

  const artworkType = artwork.artwork_type || 'print';
  const isPrint = artworkType === 'print';
  const isSold = artwork.sold === true;

  const handleAddToCart = () => {
    addItem({
      artwork_id: artwork.id,
      title: artwork.title,
      size: selectedVariant.name,
      quantity: quantity,
      unit_price: selectedVariant.price,
      image: artwork.image,
      artwork_type: artworkType,
    });
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleRequestQuote = () => {
    setShowQuoteModal(true);
  };

  const hoverImage = artwork.hoverImage || artwork.hover_image;

  // Determine price display
  const priceType = artwork.price_type || artwork.priceType || 'from';
  const showFromPrice = priceType === 'from' && sizeVariants.length > 1;

  // SEO product data
  const productSEO = {
    title: artwork.title,
    description: artwork.description,
    image: getImageUrl(artwork.image),
    price: lowestPrice,
    currency: 'SEK',
    inStock: artwork.in_stock !== false,
    sold: isSold,
    artworkType: artworkType
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        url={`/product/${slug}`}
        title={artwork.title}
        description={artwork.description || `${artwork.title} - ${artworkType === 'painting' ? 'Original painting' : 'Fine art print'} by Gorito. ${collection ? `Part of the ${collection.name}.` : ''}`}
        image={getImageUrl(artwork.image)}
        type="product"
        product={productSEO}
      />
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <Link to="/collections" className="inline-flex items-center text-sm text-gray-500 hover:text-black transition-colors">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to collections
          </Link>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Section */}
          <div className="space-y-4">
            <div className="overflow-hidden rounded-lg bg-gray-100">
              <ProtectedImage
                src={getImageUrl(artwork.image)}
                alt={artwork.title}
                className="w-full h-auto object-contain"
              />
            </div>
            {hoverImage && (
              <div className="overflow-hidden rounded-lg bg-gray-100">
                <ProtectedImage
                  src={getImageUrl(hoverImage)}
                  alt={`${artwork.title} - alternate view`}
                  className="w-full h-auto object-contain"
                />
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="lg:pl-8">
            {collection && (
              <Link 
                to={`/collections/${collection.slug}`}
                className="text-sm text-gray-500 hover:text-black transition-colors"
              >
                {collection.name}
              </Link>
            )}
            
            <h1 className="text-3xl md:text-4xl font-light text-gray-900 mt-2 mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              {artwork.title}
            </h1>

            {/* Price Display */}
            <div className="mb-6">
              {isSold ? (
                <div className="flex items-center gap-3">
                  <span className="bg-red-600 text-white px-3 py-1 text-sm font-bold tracking-wider">
                    SOLD
                  </span>
                  <p className="text-xl text-gray-500 line-through">
                    {formatPrice(selectedVariant.price, currency)}
                  </p>
                </div>
              ) : showFromPrice ? (
                <p className="text-2xl text-gray-900">
                  From {formatPrice(lowestPrice, currency)}
                </p>
              ) : (
                <p className="text-2xl text-gray-900">
                  {formatPrice(selectedVariant.price, currency)}
                </p>
              )}
            </div>

            {/* Artwork Type Badge */}
            <div className="mb-4">
              <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                isPrint 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'bg-amber-100 text-amber-800'
              }`}>
                {isPrint ? 'Print' : 'Original Painting'}
              </span>
            </div>

            <p className="text-gray-600 mb-8 leading-relaxed">
              {artwork.description}
            </p>

            {/* Size Selector with Prices */}
            {sizeVariants.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Size
                </label>
                <div className="flex flex-wrap gap-3">
                  {sizeVariants.map((variant, index) => (
                    <button
                      key={variant.name}
                      onClick={() => setSelectedSizeIndex(index)}
                      className={`px-4 py-2 text-sm border rounded-md transition-all ${
                        selectedSizeIndex === index
                          ? 'border-black bg-black text-white'
                          : 'border-gray-300 text-gray-700 hover:border-black'
                      }`}
                    >
                      <span className="font-medium">{variant.name}</span>
                      {sizeVariants.length > 1 && (
                        <span className={`ml-2 ${selectedSizeIndex === index ? 'text-gray-300' : 'text-gray-500'}`}>
                          {formatPrice(variant.price, currency)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity Selector - Only for Prints */}
            {isPrint && !isSold && (
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Quantity
                </label>
                <div className="flex items-center border border-gray-300 rounded-md w-fit">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 text-gray-500 hover:text-black transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="px-6 py-3 text-center min-w-[60px]">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 text-gray-500 hover:text-black transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Add to Cart / Request Quote Button */}
            {isSold ? (
              <div className="w-full py-4 text-sm tracking-wide rounded-md bg-gray-200 text-gray-500 text-center font-medium">
                This artwork has been sold
              </div>
            ) : isPrint ? (
              <button
                onClick={handleAddToCart}
                data-testid="add-to-cart-btn"
                className={`w-full py-4 text-sm tracking-wide rounded-md transition-all flex items-center justify-center gap-2 ${
                  addedToCart
                    ? 'bg-green-600 text-white'
                    : 'bg-black text-white hover:bg-gray-800'
                }`}
              >
                {addedToCart ? (
                  <>
                    <Check className="w-5 h-5" />
                    Added to cart
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5" />
                    Add to cart
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleRequestQuote}
                data-testid="request-quote-btn"
                className="w-full py-4 text-sm tracking-wide rounded-md transition-all flex items-center justify-center gap-2 bg-amber-600 text-white hover:bg-amber-700"
              >
                <Send className="w-5 h-5" />
                Request Shipping Quote
              </button>
            )}

            {/* Shipping Info */}
            {!isSold && isPrint && (
              <p className="mt-3 text-sm text-gray-500 text-center">
                Flat rate shipping: 230 kr (UK/EU) | 290 kr (US) | 345 kr (Worldwide)
              </p>
            )}
            {!isSold && !isPrint && (
              <p className="mt-3 text-sm text-amber-600 text-center">
                Original paintings require a custom shipping quote due to special packaging needs.
              </p>
            )}

            {/* Additional Info - Only show for prints */}
            {isPrint && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="space-y-4 text-sm text-gray-600">
                  <p>
                    <strong className="text-gray-900">Shipping:</strong> Ships within 3-5 business days
                  </p>
                  <p>
                    <strong className="text-gray-900">Returns:</strong> 30-day return policy
                  </p>
                  <p>
                    <strong className="text-gray-900">Quality:</strong> Museum-quality giclée prints on premium paper
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        <div className="mt-20">
          <h2 className="text-2xl font-light text-gray-900 mb-8" style={{ fontFamily: 'Georgia, serif' }}>
            You may also like
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {artworks
              .filter(a => a.id !== artwork.id)
              .slice(0, 4)
              .map((relatedArtwork) => {
                const priceType = relatedArtwork.price_type || relatedArtwork.priceType || 'from';
                const relatedLowest = getLowestPrice(relatedArtwork.sizes, relatedArtwork.price);
                return (
                  <Link key={relatedArtwork.id} to={`/product/${relatedArtwork.slug}`} className="group">
                    <div className="aspect-square overflow-hidden rounded-md bg-gray-100">
                      <img
                        src={getImageUrl(relatedArtwork.image)}
                        alt={relatedArtwork.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="mt-3 text-sm text-gray-900 group-hover:underline line-clamp-1">
                      {relatedArtwork.title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      {priceType === 'fixed' ? '' : 'From '}{formatPrice(relatedLowest, currency)}
                    </p>
                  </Link>
                );
              })}
          </div>
        </div>
      </main>
      <Footer />

      {/* Quote Request Modal */}
      <QuoteRequestModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        artwork={artwork}
      />
    </div>
  );
};

export default ProductPage;
